import {Button} from "@zendeskgarden/react-buttons";
import {Dropdown, HeaderIcon, HeaderItem, Item, Menu, Separator, Trigger} from "@zendeskgarden/react-dropdowns";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {ReactElement, useContext} from "react";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";
import * as css from "./styles/css.module.scss";
import {BsGearFill, BsGearWideConnected} from "react-icons/bs";
import {useHistory} from "react-router-dom";
import {AiOutlineLogout} from "react-icons/ai";
import {COLORS} from "../../../../constants/Colors";
import {useLogoutConfirmation} from "../logout-button/useLogoutConfirmation";
import {AuthContext, AuthUser} from "../../../../contexts/AuthContext";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";
import {
    checkAuthorization,
    hasAcquisitionPropertyAccess,
    hasAnyAuthorization,
    userIsAdmin,
    userIsInternal
} from "../../../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../../../authorization/Authorizations";
import {PropertyManagementSystem} from "../../../../__generated__/generated_types";
import {isAcquisitionPropertiesEnabled, isInitialGprSetupEnabled} from "../../../../global/feature_checkers";

export function SettingsNavMenu(): ReactElement {
    const history = useHistory();
    const {user} = useContext(AuthContext);
    const {properties} = useProperties();

    const buttonClass = (history.location.pathname.startsWith("/settings/") || history.location.pathname.startsWith("/admin/")) ? css.focusedButton : undefined;

    const {requestLogout, logoutConfirmation} = useLogoutConfirmation();

    let highlightedIndex = 9999999;
    if (history.location.pathname.includes("financial-metrics-management")) {
        highlightedIndex = 1;
    } else if (history.location.pathname.includes("property-plan-management")) {
        highlightedIndex = 3;
    } else if (history.location.pathname.includes("lock-management")) {
        highlightedIndex = 5;
    } else if (history.location.pathname.includes("budget-season-management")) {
        highlightedIndex = 7;
    } else if (history.location.pathname.includes("coa-management")) {
        highlightedIndex = 9;
    } else if (history.location.pathname.includes("account-tags-management")) {
        highlightedIndex = 11;
    } else if (history.location.pathname.includes("custom-item-management")) {
        highlightedIndex = 13;
    } else if (history.location.pathname.includes("line-items-management")) {
        highlightedIndex = 14;
    } else if (history.location.pathname.includes("unit-type-management")) {
        highlightedIndex = 15;
    } else if (history.location.pathname.includes("users")) {
        highlightedIndex = 17;
    }

    function hasAdminOrAuthorization(user: AuthUser | null, authorizations?: Authorizations[]): boolean {
        if(authorizations) {
            return userIsAdmin(user) || hasAnyAuthorization(user, authorizations);
        }
        return userIsAdmin(user);
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Dropdown
                onSelect={url => history.push(url)}
                highlightedIndex={highlightedIndex}
            >
                <Trigger>
                    <Button isBasic className={`${buttonClass} ${css.navIconButton}`}>
                        <BsGearFill size={24} />
                    </Button>
                </Trigger>

                <Menu className={css.navMenuWrapper} maxHeight="90vh">
                    {hasAdminOrAuthorization(user, [Authorizations.AccessPropertyPlanManagement, Authorizations.AccessBudgetSeasonManagement]) &&
                        <>
                            <HeaderItem hasIcon>
                                <HeaderIcon>
                                    <BsGearWideConnected color={COLORS.GREY_300} />
                                </HeaderIcon>
                                <span className={css.menuHeaderItem}>Settings</span>
                            </HeaderItem>
                            <Separator />
                        </>
                    }
                    {userIsAdmin(user) && <Item value="/settings/financial-metrics-management">Financial Metrics Management</Item>}
                    {checkAuthorization(user, Authorizations.AccessPropertyPlanManagement) && <Item value="/settings/property-plan-management">Property Plan Management</Item>}
                    {userIsAdmin(user) &&
                        <>
                            {/*<Item value="/settings/bulk-assumptions">Bulk Set Revenue Assumptions</Item>*/}
                            <Item value="/settings/lock-management">Lock Management</Item>
                        </>
                    }
                    {userIsAdmin(user) &&
                        <Item value="/settings/bulk-assumptions">Bulk Update Revenue Assumptions</Item>
                    }
                    {checkAuthorization(user, Authorizations.AccessBudgetSeasonManagement) && <Item value="/settings/budget-season-management">Budget Season Management</Item>}
                    {hasAdminOrAuthorization(user, [Authorizations.AccessPropertyPlanManagement, Authorizations.AccessBudgetSeasonManagement]) && <Separator />}
                    {userIsAdmin(user) &&
                        <>
                            <Item value="/settings/coa-management">Chart of Accounts Management</Item>
                            <Item value="/settings/account-tags-management">Account Tags Management</Item>
                            <Separator />
                            <Item value="/settings/custom-item-management">Custom Item Management</Item>
                            <Item value="/admin/line-items-management">Line Items Management</Item>
                            <Item value="/admin/global-overrides">Global Overrides</Item>
                            <Separator />
                            <Item value="/settings/unit-type-management">Unit Type Management</Item>
                            <Item value="/settings/property-management">Property Management</Item>
                            <Item value="/settings/template-properties">Template Properties</Item>
                            <Separator />
                            <Item value="/settings/payroll-settings">Payroll Settings</Item>
                            <Item value="/settings/payroll-jurisdictions">Payroll Jurisdictions</Item>
                            <Item value="/settings/payroll-positions">Payroll Positions</Item>
                            <Item value="/settings/payroll-defaults">Payroll Defaults</Item>
                            <Separator />
                            <Item value="/settings/executive-summary-report-management">Executive Summary Report Management</Item>
                            <Separator />
                            <Item value="/admin/users">User Management</Item>
                            <Item value="/admin/audit-log">Audit Log</Item>
                            <Separator />
                            {properties && properties.findIndex(p => p.propertyManagementSystem === PropertyManagementSystem.Entrata) !== -1 &&
                                <>
                                    <Item value="/admin/send_budget">Send Budget to PMS</Item>
                                    <Separator />
                                </>
                            }
                        </>
                    }
                    {
                        hasAcquisitionPropertyAccess(user) && isAcquisitionPropertiesEnabled(user) &&
                            <Item value="/settings/acquisition-properties">Budget Builder</Item>
                    }
                    {userIsInternal(user) &&
                        <Item value="/settings/internal/client-features">Client Features</Item>
                    }
                    {userIsInternal(user) &&
                        <Item value="/settings/internal/client-unit-count">Client Unit Count</Item>
                    }
                    {(userIsInternal(user) || (userIsAdmin(user) && isInitialGprSetupEnabled(user))) &&
                        <Item value="/settings/gpr-entity-groups">Initial GPR Setup</Item>
                    }
                    <Button isStretched className={css.logoutButton} onClick={() => history.push("/budget-completion")}>
                        Budget Status
                    </Button>
                    <Button isStretched className={css.logoutButton} onClick={requestLogout}>
                        <AiOutlineLogout size={22} className={css.logoutButtonIcon} />

                        Logout
                    </Button>
                </Menu>
            </Dropdown>

            {logoutConfirmation}
        </ThemeProvider>
    );
}
